import { Controller } from "@hotwired/stimulus"

// Inline WYSIWYG editing for the review screen: the rendered changelog IS the
// editor. Clicking a title or item line swaps the rendered (markdown) display
// for an autosizing textarea of the raw markdown. Enter or blur commits via a
// JSON PATCH; Escape cancels. On success the server returns the re-rendered
// inline HTML, which replaces the display in place (no full reload).
//
// Each editable wrapper carries: data-url, data-param-key (e.g.
// "changelog_item[body]"), data-raw (current markdown source), and contains a
// [data-role="display"] + [data-role="input"] pair.
export default class extends Controller {
  edit(event) {
    const wrapper = event.currentTarget.closest(
      "[data-changelog-editor-target='editable']",
    )
    if (!wrapper) return
    const display = wrapper.querySelector("[data-role='display']")
    const input = wrapper.querySelector("[data-role='input']")
    input.value = wrapper.dataset.raw || display.textContent.trim()
    display.classList.add("hidden")
    input.classList.remove("hidden")
    this.autosize(input)
    input.focus()
    input.setSelectionRange(input.value.length, input.value.length)
  }

  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      event.target.blur() // triggers commit
    } else if (event.key === "Escape") {
      this.cancelling = true
      event.target.blur()
    } else {
      this.autosize(event.target)
    }
  }

  async commit(event) {
    const input = event.target
    const wrapper = input.closest("[data-changelog-editor-target='editable']")
    const display = wrapper.querySelector("[data-role='display']")

    if (this.cancelling) {
      this.cancelling = false
      this.restore(wrapper, input, display)
      return
    }

    const value = input.value.trim()
    if (value === (wrapper.dataset.raw || "").trim()) {
      this.restore(wrapper, input, display)
      return
    }

    const body = new URLSearchParams()
    body.append(wrapper.dataset.paramKey, value)

    try {
      const response = await fetch(wrapper.dataset.url, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.ok) {
        wrapper.dataset.raw = value
        if (data.html) display.innerHTML = data.html
      }
    } catch (e) {
      // leave the previous display in place on failure
    } finally {
      this.restore(wrapper, input, display)
    }
  }

  restore(wrapper, input, display) {
    input.classList.add("hidden")
    display.classList.remove("hidden")
  }

  autosize(el) {
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
